import React from "react";
import { Check, ArrowRight, Settings, Zap, Clock, BookOpen, Car } from "lucide-react";
import { MANUAL_CAR_IMAGES, AUTOMATIC_CAR_IMAGES } from "../lib/assets";
import { useLang } from "../context/LangContext";

const Pricing = () => {
  const { t } = useLang();

  const scrollToContact = (preset) => {
    const el = document.getElementById("contact");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => {
        const event = new CustomEvent("preset-transmission", { detail: preset });
        window.dispatchEvent(event);
      }, 400);
    }
  };

  const packages = [
    {
      key: "manual",
      icon: <Settings className="w-6 h-6" />,
      data: t.pricing.manual,
      image: MANUAL_CAR_IMAGES[3],
      highlight: true,
      testid: "pricing-manual-card",
      preset: "manual",
    },
    {
      key: "automatic",
      icon: <Zap className="w-6 h-6" />,
      data: t.pricing.automatic,
      image: AUTOMATIC_CAR_IMAGES[0],
      highlight: false,
      testid: "pricing-auto-card",
      preset: "automatic",
    },
  ];

  const extraIcons = {
    extra_driving: <Clock className="w-6 h-6" />,
    extra_legislation: <BookOpen className="w-6 h-6" />,
    exam_car: <Car className="w-6 h-6" />,
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 sm:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs sm:text-sm uppercase tracking-[0.22em] font-bold text-brand-orange mb-4">
            {t.pricing.eyebrow}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-navy leading-tight tracking-tight">
            {t.pricing.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed">
            {t.pricing.subtitle}
          </p>
        </div>

        {/* Section divider */}
        <div className="mt-12 mb-6 flex items-center gap-4">
          <span className="font-display text-sm sm:text-base font-bold uppercase tracking-[0.2em] text-brand-navy">
            {t.pricing.mainTitle}
          </span>
          <span className="flex-1 h-px bg-brand-border-soft" />
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {packages.map((pkg) => (
            <div
              key={pkg.key}
              data-testid={pkg.testid}
              className={`group relative rounded-3xl overflow-hidden lift-on-hover ${
                pkg.highlight
                  ? "bg-brand-navy text-white border border-brand-navy"
                  : "bg-brand-cream border border-brand-border-soft"
              }`}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={pkg.image}
                  alt={pkg.data.car}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold ${
                    pkg.highlight ? "bg-brand-orange text-white" : "bg-brand-navy text-white"
                  }`}>
                    {pkg.data.badge}
                  </span>
                </div>
              </div>

              <div className="p-7 sm:p-9">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    pkg.highlight ? "bg-brand-orange text-white" : "bg-brand-orange/15 text-brand-orange-deep"
                  }`}>
                    {pkg.icon}
                  </div>
                  <div>
                    <h3 className={`font-display text-2xl font-bold ${pkg.highlight ? "text-white" : "text-brand-navy"}`}>
                      {pkg.data.title}
                    </h3>
                    <p className={`text-sm font-medium ${pkg.highlight ? "text-white/70" : "text-brand-muted"}`}>
                      {pkg.data.car}
                    </p>
                  </div>
                </div>

                <div className="flex items-baseline gap-2 mt-6 pb-6 border-b border-dashed border-current/15">
                  <div className="flex flex-col">
                    {pkg.data.oldPrice && (
                      <span className={`font-display text-xl sm:text-2xl font-bold line-through leading-none mb-1 ${
                        pkg.highlight ? "text-white/50" : "text-brand-muted"
                      }`}>
                        {pkg.data.oldPrice} {pkg.data.currency}
                      </span>
                    )}
                    <div className="flex items-baseline gap-2">
                      <span className={`font-display text-5xl sm:text-6xl font-extrabold tracking-tight ${
                        pkg.highlight ? "text-brand-orange" : "text-brand-navy"
                      }`}>
                        {pkg.data.price}
                      </span>
                      <span className={`font-display text-xl font-bold ${pkg.highlight ? "text-white" : "text-brand-navy"}`}>
                        {pkg.data.currency}
                      </span>
                    </div>
                  </div>
                </div>

                <ul className="mt-6 space-y-3">
                  {pkg.data.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                        pkg.highlight ? "text-brand-orange" : "text-brand-orange-deep"
                      }`} strokeWidth={3} />
                      <span className={`text-sm sm:text-base ${pkg.highlight ? "text-white/90" : "text-brand-navy"}`}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => scrollToContact(pkg.preset)}
                  data-testid={`pricing-cta-${pkg.preset}`}
                  className={`mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-semibold transition-all hover:-translate-y-0.5 ${
                    pkg.highlight
                      ? "bg-brand-orange hover:bg-brand-orange-deep text-white"
                      : "bg-brand-navy hover:bg-brand-navy-deep text-white"
                  }`}
                >
                  {pkg.data.cta}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Extras */}
        <div className="mt-20 mb-6 flex items-center gap-4">
          <span className="font-display text-sm sm:text-base font-bold uppercase tracking-[0.2em] text-brand-navy">
            {t.pricing.extrasTitle}
          </span>
          <span className="flex-1 h-px bg-brand-border-soft" />
        </div>
        <p className="text-brand-muted text-base sm:text-lg leading-relaxed max-w-2xl mb-8">
          {t.pricing.extrasSubtitle}
        </p>

        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {t.pricing.extras.map((extra, i) => (
            <div
              key={extra.code}
              data-testid={`pricing-extra-${extra.code}`}
              className="group relative bg-brand-cream border border-brand-border-soft rounded-3xl p-7 lift-on-hover flex flex-col"
            >
              <div className="w-12 h-12 rounded-2xl bg-brand-orange/15 text-brand-orange-deep flex items-center justify-center mb-5 group-hover:bg-brand-orange group-hover:text-white transition-colors">
                {extraIcons[extra.code]}
              </div>

              <h3 className="font-display text-lg sm:text-xl font-bold text-brand-navy leading-snug">
                {extra.title}
              </h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed flex-1">
                {extra.desc}
              </p>

              <div className="mt-6 pt-5 border-t border-dashed border-brand-border-soft flex items-baseline justify-between">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-display text-3xl sm:text-4xl font-extrabold text-brand-navy">
                    {extra.price}
                  </span>
                  <span className="font-display text-base font-bold text-brand-navy">
                    {extra.currency}
                  </span>
                </div>
                <button
                  onClick={() => scrollToContact(extra.code)}
                  data-testid={`pricing-extra-cta-${extra.code}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-orange-deep hover:text-brand-orange-deep group/btn"
                >
                  {extra.cta}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
